import React from 'react';
import { FileText, Smartphone, LayoutDashboard, Database, Scale, AlertCircle, Ban } from 'lucide-react';
import LegalLayout from '../../components/LegalLayout';

const TermsOfServicePage: React.FC = () => {
  const lastUpdate = "24 de Março de 2026";

  return (
    <LegalLayout
      title="Termos de Serviço"
      description="Regras de utilização do Gastroflow como plataforma SaaS de gestão inteligente para estabelecimentos comerciais."
    >
      <div className="space-y-12 text-secondary/90 leading-relaxed">

        {/* Intro */}
        <section className="bg-white/5 border border-white/10 rounded-3xl p-8 mb-8">
          <div className="flex items-center gap-4 mb-4 text-primary">
            <FileText className="w-8 h-8" />
            <h2 className="text-2xl font-black text-white">Aceite dos Termos</h2>
          </div>
          <p>
            Ao utilizar o <strong>Gastroflow</strong> (referido como "Gastroflow", "Serviço" ou "Garçom Digital"), você (doravante "Estabelecimento") concorda integralmente com estes Termos de Serviço. O uso indevido ou o descumprimento destas cláusulas resultará na suspensão imediata da conta.
          </p>
          <p className="mt-4 text-sm text-secondary/50 font-medium italic">
            Versão do documento: 1.2 ({lastUpdate})
          </p>
        </section>

        {/* 1. Objeto */}
        <section className="space-y-4">
          <div className="flex items-center gap-3 text-white border-b border-white/10 pb-2">
            <LayoutDashboard className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold">1. Objeto do Serviço</h2>
          </div>
          <p>O Gastroflow é uma plataforma SaaS (Software as a Service) voltada para a gestão de restaurantes, pizzarias e similares, oferecendo as seguintes funcionalidades principais:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Cardápio Digital:</strong> Criação e gerenciamento dinâmico de produtos com suporte a modelos 3D/AR.</li>
            <li><strong>Gestão de Pedidos:</strong> Sincronização em tempo real entre salão, cozinha e administrativo.</li>
            <li><strong>Sistema de Reservas:</strong> Controle centralizado de mesas e horários.</li>
            <li><strong>Painel de Desempenho:</strong> Relatórios consolidados de vendas e métricas de operação.</li>
          </ul>
        </section>

        {/* 2. Responsabilidades do Estabelecimento */}
        <section className="space-y-4">
          <div className="flex items-center gap-3 text-white border-b border-white/10 pb-2">
            <AlertCircle className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold">2. Suas Responsabilidades</h2>
          </div>
          <p>O Estabelecimento declara ser civilmente responsável pelas seguintes ações:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Veracidade dos Dados:</strong> Informações de cardápio, preços e cobranças são de inteira responsabilidade do restaurante.</li>
            <li><strong>Gestão de Acesso:</strong> Manter a confidencialidade das senhas e tokens de acesso à plataforma.</li>
            <li><strong>Atendimento ao Cliente:</strong> O Gastroflow atua como ferramenta tecnológica; o suporte e a entrega de produtos e serviços são de responsabilidade do Estabelecimento.</li>
            <li><strong>Conformidade Legal:</strong> O restaurante deve operar conforme as leis sanitárias e fiscais vigentes.</li>
          </ul>
        </section>

        {/* 3. Integrações de Terceiros */}
        <section className="space-y-4">
          <div className="flex items-center gap-3 text-white border-b border-white/10 pb-2">
            <Smartphone className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold">3. Serviços Integrados</h2>
          </div>
          <p>
            O Gastroflow integra-se com serviços externos (como WhatsApp API, Mercado Pago e Stripe). O funcionamento destas integrações depende da disponibilidade e dos termos específicos de cada provedor:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Não nos responsabilizamos por instabilidades oriundas da Meta/WhatsApp Business.</li>
            <li>Cobranças de taxas de intermediação de pagamentos são responsabilidade exclusiva da processadora (ex: Mercado Pago).</li>
            <li>A revogação de chaves de API pelo Estabelecimento impacta diretamente o funcionamento do serviço.</li>
          </ul>
        </section>

        {/* 4. Uso Proibido */}
        <section className="space-y-4">
          <div className="flex items-center gap-3 text-white border-b border-white/10 pb-2">
            <Ban className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold">4. Conduta Proibida</h2>
          </div>
          <p>É expressamente vedado o uso do Gastroflow para:</p>
          <ul className="list-disc pl-6 space-y-2 text-red-100/80">
            <li>Atividades fraudulentas ou lavagem de dinheiro.</li>
            <li>Práticas de Spam ou envio massivo de mensagens via WhatsApp sem consentimento.</li>
            <li>Engenharia reversa no código-fonte da aplicação ou backend.</li>
            <li>Tratamento ilegal de dados de clientes finais que violem a LGPD.</li>
          </ul>
        </section>

        {/* 5. Suspensão e Encerramento */}
        <section className="space-y-4">
          <div className="flex items-center gap-3 text-white border-b border-white/10 pb-2">
            <Scale className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold">5. Disponibilidade e Rescisão</h2>
          </div>
          <p>
            Garantimos um SLA (disponibilidade) de 99,8% de nossos sistemas principais. O acesso poderá ser suspendido por:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Atraso superior a 5 dias no pagamento da assinatura (SaaS).</li>
            <li>Violação de segurança confirmada oriunda do Estabelecimento.</li>
            <li>Rescisão amigável mediante solicitação de cancelamento.</li>
          </ul>
        </section>

        {/* 6. Dados e Armazenamento */}
        <section className="space-y-4">
          <div className="flex items-center gap-3 text-white border-b border-white/10 pb-2">
            <Database className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-bold">6. Retenção de Dados em Cancelamentos</h2>
          </div>
          <p>
            Em caso de encerramento do contrato, os dados operacionais do restaurante serão mantidos em backup por 30 dias para fins de exportação. Após este período, a exclusão será definitiva para segurança do Estabelecimento.
          </p>
        </section>

        <section className="pt-8 border-t border-white/10 text-center">
          <p className="text-sm text-secondary/60">
            Dúvidas sobre os Termos? <br />
            <a href="mailto:contato@garcomdigital.site" className="text-primary font-bold hover:underline">contato@garcomdigital.site</a>
          </p>
        </section>
      </div>
    </LegalLayout>
  );
};

export default TermsOfServicePage;
